import React, { useState, useEffect, useRef } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import {
  ButtonSizeOptions,
  PrimaryButton,
  SecondaryButton
} from "@loanbossinc/component-library";
import RichTextEditor from "components/Global/RichTextEditor";
import moment from "moment";
import { authorizedRequest } from "utils/api";
import numeral from "numeral";
import { ToastError } from "components/Global/Toasts/Toasty";
import { getPrepayment, getValuationSensitivity, getMasterContactList } from "pages/campaigns/emailCampaign.store";

import { PreviewBase } from "../../campaigns/templates/PreviewBase";

import { CampaignType, CampaignTypes } from "./constants";

import "./emailTemplateStyles.css";
import { RecipientRow } from "./";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const borderStyle = "1px solid #DADCE0";

export const EmailTemplate = ({
  deal,
  brokerEmail,
  onCancel,
  companyLogo,
  companyName,
  companyPrimaryColor,
  companySecondaryColor,
  getMasterContactList,
  getPrepayment,
  getValuationSensitivity,
  emailCampaign
}) => {
  const staticElement = useRef();
  const logoRef = useRef();
  const minCapRate = 0.03;
  const maxCapRate = 0.06;

  const [dealName, setDealName] = useState("");
  const [dealId, setDealId] = useState(0);
  const [campaignType, setCampaignType] = useState("");
  const [subject, setSubject] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [contacts, setContacts] = useState([]);
  const [contactInputType, setContactInputType] = useState("");
  const [ccContactInputType, setCcContactInputType] = useState("");
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedCcContacts, setSelectedCcContacts] = useState([]);
  const [emailInput, setEmailInput] = useState("");
  const [emailCcInput, setEmailCcInput] = useState("");
  const [ccRowIsPresent, setCcRowIsPresent] = useState(true);
  const [pin, setPin] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [maxLogoDimensions, setMaxLogoDimensions] = useState({ height: "40px", width: "40px" });
  const [fetchCampaignFunction, setFetchCampaignFunction] = useState(() => () => undefined);

  const isPrepaymentCampaign = campaignType === CampaignType.PREPAYMENT.name;
  const isValuationSensitivityCampaign = deal?.campaignType === CampaignType.VALUATION_SENSITIVITY.name;

  useEffect(() => {
    setPin(generatePinOfLength(6));
  }, []);

  useEffect(() => {
    const formattedContacts = emailCampaign.masterContactList.map(({ contactName, email, included, company }) => ({
      name: contactName,
      email,
      included: !included,
      companyId: company.companyId
    })).filter(({ included, companyId }) => included && deal?.sponsorCompanyId === companyId);
    setContacts(formattedContacts);
  }, [emailCampaign.masterContactList]);

  useEffect(() => {
    if (isValuationSensitivityCampaign) {
      getValuationSensitivity({ dealId: deal?.id });
    }
  }, [deal?.campaignType, isValuationSensitivityCampaign]);

  useEffect(() => {
    if (Boolean(campaignType) && Boolean(dealName)) {
      setSubject(`${campaignType} - ${dealName}`);
    }

    if (!campaignType && Boolean(dealName)) {
      setSubject(`${dealName}`);
    }

    if (Boolean(campaignType) && !dealName) {
      setSubject(`${campaignType}`);
    }

    if (Boolean(campaignType) && Boolean(dealName)) {
      setSubject("");
    }
  }, [campaignType, dealName]);

  useEffect(() => {
    getMasterContactList();
    setDealName(deal?.currentDeal?.name ?? "");
    setCampaignType(deal?.campaignType ?? "");
    setDealId(deal?.id ?? 0);
  }, [deal]);

  useEffect(() => {
    if (logoRef?.current) {
      const { height = 0, width = 0 } = logoRef?.current;
      const isRectangularShape = width >= 1.5 * height;
      isRectangularShape ? setMaxLogoDimensions({ height: "40px", width: "105px" }) : setMaxLogoDimensions({ height: "40px", width: "40px" });
    }
  }, [companyLogo]);

  useEffect(() => {
    if (isValuationSensitivityCampaign) {
      setFetchCampaignFunction(() => () => getValuationSensitivity({ dealId }));
    }
    if (isPrepaymentCampaign) {
      setFetchCampaignFunction(() => () => getPrepayment({ dealId }));
    }
  }, [campaignType]);

  useEffect(() => {
    if (!ccRowIsPresent) {
      setCcContactInputType("");
      setSelectedCcContacts([]);
      setEmailCcInput("");
    }
  }, [ccRowIsPresent]);

  const onEditorStateChange = change => setBodyText(change);
  const onSubjectChange = e => setSubject(e.target.value);
  const onPreviewPDFClicked = () => setShowPreview(true);

  const DoNotClick = ({ children }) => {
    useEffect(() => {
      const elemCollection = document.getElementsByClassName("do-not-click");
      const doNotClick = e => e.preventDefault();
      for (const element of elemCollection) {
        element.addEventListener("click", doNotClick);
      }

      return () => {
        for (const element of elemCollection) {
          element.removeEventListener("click", doNotClick);
        }
      };
    }, []);

    return <>{children}</>;
  };

  const generatePinOfLength = length => Array(length).fill(0).map(() => Math.floor(Math.random() * 10)).join("");
  const getCampaignTypeId = campaignTypeName => {
    switch (campaignTypeName) {
      case CampaignType.PREPAYMENT.name:
        return CampaignType.PREPAYMENT.id;
      case CampaignType.VALUATION_SENSITIVITY.name:
        return CampaignType.VALUATION_SENSITIVITY.id;
      default:
        return "";
    }
  };
  const getRoute = () => {
    switch (campaignType) {
      case CampaignType.PREPAYMENT.name:
        return "prepayment";
      case CampaignType.CASH_OUT_PROCEEDS.name:
        return "refi-cashout";
      case CampaignType.REFI_BREAKEVEN.name:
        return "refi-breakeven";
      case CampaignType.VALUATION_SENSITIVITY.name:
        return "valuation-sensitivity";
      case CampaignType.DEBT_WITH_US.name:
        return "debt-with-us";
      default:
        return "prepayment";
    }
  };

  const getLink = () => {
    const route = getRoute();

    return `${window.location.origin}/${route}/snapshot/${dealId}`;
  };

  const generateButtonHtml = color =>
  // I"m sorry. I know. I couldn"t find any other way. Please forgive me.
  ({
    __html: `
        <!--[if mso]> 
          <v:roundrect xmlns:v="urn:schemas-microsoft-com:vml" xmlns:w="urn:schemas-microsoft-com:office:word" href="${getLink()}?ssId=%NA%" style="height:40px;v-text-anchor:middle;width:300px;" arcsize="10%" stroke="f" fillcolor="#${color}"> 
            <w:anchorlock/> 
              <center style="color:white;font-family:sans-serif;font-size:16px;font-weight:bold;"> 
                View Analysis 
              </center> 
          </v:roundrect> 
        <![endif]-->
        <![if !mso]>
          <table style="margin: 0 auto;" cellspacing="0" cellpadding="0"> <tr> 
            <td align="center" width="300" height="40" bgcolor="#${color}" style="-webkit-border-radius: 5px; white-space: initial; -moz-border-radius: 5px; border-radius: 5px; color: #8C4845; display: block;">
              <a href="${getLink()}?ssId=%NA%" class="do-not-click" style="font-size:16px; font-weight: bold; font-family:sans-serif; text-decoration: none; line-height:40px; width:100%; display:inline-block">
                <span style="color: white;">
                  View Analysis
                </span>
              </a>
            </td> 
          </tr> </table> 
        <![endif]>       
        ` });
  const getLabel1 = () => CampaignTypes.find(c => c.name === campaignType)?.label1;
  const getLabel2 = () => CampaignTypes.find(c => c.name === campaignType)?.label2;

  const getValuationCapRateData = (_emailCampaign, capRateToFind) => {
    if (_emailCampaign?.valuationSensitivity?.valuationSensitivityToCapRate) {
      const data = _emailCampaign.valuationSensitivity.valuationSensitivityToCapRate;

      return data.find(({ capRate }) => capRate === capRateToFind);
    }

    return "";
  };

  const getValue1 = () => {
    switch (campaignType) {
      case CampaignType.PREPAYMENT.name:
        return `$${numeral(deal?.currentDeal?.prepaymentPenalty?.amount ?? 0).format("0,0")}`;
      case CampaignType.CASH_OUT_PROCEEDS.name:
        return `$${numeral(deal?.refinanceEstimates?.cashout?.amount ?? 0).format("0,0")}`;
      case CampaignType.REFI_BREAKEVEN.name:
        return `${numeral(deal?.refinanceEstimates?.breakevenSpread ?? 0).format("0,0.00%")}`;
      case CampaignType.VALUATION_SENSITIVITY.name:
        const minCap = getValuationCapRateData(emailCampaign, minCapRate);
        if (minCap) {
          return `${numeral(minCap.valuation).format("$0,0")}`;
        }

        return "";
      default:
        return "";
    }
  };

  const getValue2 = () => {
    switch (campaignType) {
      case CampaignType.PREPAYMENT.name:
        return `${numeral(deal?.currentDeal?.prepaymentPenalty?.percentOfLoanAmount ?? 0).format("0,0.00%")}`;
      case CampaignType.CASH_OUT_PROCEEDS.name:
        return `${numeral(deal?.refinanceEstimates?.cashout?.percentChange ?? 0).format("0,0.00%")}`;
      case CampaignType.REFI_BREAKEVEN.name:
        return `${numeral(deal?.refinanceEstimates?.breakevenTreasury ?? 0).format("0,0.00%")}`;
      case CampaignType.VALUATION_SENSITIVITY.name:
        const maxCap = getValuationCapRateData(emailCampaign, maxCapRate);
        if (maxCap) {
          return `${numeral(maxCap.valuation).format("$0,0")}`;
        }

        return "";
      default:
        return "";
    }
  };

  const sendEmail = () => {
    const requests = [];
    let url = "";

    /*
     * TODO: This needs updated to include the CC'ed contacts.
     * This seems to indicate that we're only sending emails to one recipient at a time given the singularly mapped client
     * even if it is batched, but are we sending CC's in the same way? 20221006 TDL
     */
    const sendTo = selectedContacts.map(c => ({
      recipient: c.email,
      subject,
      replyTo: brokerEmail,
      htmlBody: bodyText + staticElement.current.innerHTML
    }));

    switch (campaignType) {
      case CampaignType.PREPAYMENT.name:
        url = `/edc/prepayment/${dealId}/send-email`;
        break;
      case CampaignType.VALUATION_SENSITIVITY.name:
        url = `/edc/valuation/${dealId}/send-email`;
        break;
      case CampaignType.DEBT_WITH_US.name:
        url = `edc/debtwithus/${deal?.sponsorCompanyId}/send-email`;
        break;
      default:
        break;
    }

    requests.push(authorizedRequest(url, "POST", { pin, sendTo, logo: companyLogo }, null, false));

    try {
      Promise.all(requests);
      onCancel();
    } catch (e) {
      ToastError("Something went wrong trying to send email, please try again");
      throw e;
    }
  };

  const sendButtonDisabled = !selectedContacts?.length || !dealId;

  return (
    <div className="email-template-container">
      {showPreview &&
        <PreviewBase
          logo={companyLogo}
          campaignType={getCampaignTypeId(campaignType)}
          fetchData={fetchCampaignFunction}
          open={showPreview}
          dealId={dealId}
          handleClose={() => setShowPreview(false)}
        />}
      <div className="email-template-body">
        <div className="email-template-title">
          {dealName}
          {deal?.sponsorCompany && ","}
          {" "}
          {deal?.sponsorCompany}
        </div>
        <RecipientRow
          actionWord="To"
          contactInputType={contactInputType}
          setContactInputType={setContactInputType}
          selectedContacts={selectedContacts}
          setSelectedContacts={setSelectedContacts}
          itemHeight={ITEM_HEIGHT}
          itemPaddingTop={ITEM_PADDING_TOP}
          contacts={contacts}
          emailInput={emailInput}
          setEmailInput={setEmailInput}
          includeCCRecipientToggle
          ccRowIsPresent={ccRowIsPresent}
          setCcRowIsPresent={setCcRowIsPresent}
        />
        {
          ccRowIsPresent &&
          <RecipientRow
            actionWord="CC"
            contactInputType={ccContactInputType}
            setContactInputType={setCcContactInputType}
            selectedContacts={selectedCcContacts}
            setSelectedContacts={setSelectedCcContacts}
            itemHeight={ITEM_HEIGHT}
            itemPaddingTop={ITEM_PADDING_TOP}
            contacts={contacts}
            emailInput={emailCcInput}
            setEmailInput={setEmailCcInput}
          />
        }
        <input
          className="email-template-subject-input"
          type="text"
          placeholder="Subject"
          value={subject}
          onChange={onSubjectChange}
        />
        <RichTextEditor
          htmlString={bodyText}
          handleUpdate={onEditorStateChange}
          maxColumns={6}
          maxLength={1000}
        />
        <DoNotClick>
          <div ref={staticElement}>
            <table align="center" cellSpacing="0" cellPadding="0" style={{ maxWidth: 660, fontFamily: "Work Sans", margin: "38px auto", borderRadius: 8, border: borderStyle, backgroundColor: `#${companyPrimaryColor}` }}>
              <tr align="center">
                <table width="100%" className="email-template-table">
                  <tr>
                    {companyLogo &&
                      <td align="left">
                        <p>
                          <img
                            className="email-template-company-logo-img"
                            ref={logoRef}
                            src={companyLogo}
                            style={{
                              maxHeight: maxLogoDimensions.height,
                              maxWidth: maxLogoDimensions.width
                            }}
                            alt={`${companyName}-logo`}
                          />
                        </p>
                      </td>}
                    <td align="right">
                      <table>
                        <tr>
                          <td align="right">
                            <p className="email-template-powered-by-p ">
                              Powered by &nbsp;
                            </p>
                          </td>
                          <td align="left">
                            <img height="18" width="100" src="https://assets.loanboss.com/loanboss-Logo-White.png" style={{ width: 100, marginBottom: -2 }} alt="loanboss-Logo-White" />
                          </td>
                        </tr>
                      </table>
                    </td>
                  </tr>
                </table>
              </tr>
              <tr style={{ marginBottom: 25, width: 422, marginLeft: "auto", marginRight: "auto", textAlign: "center", color: "black", fontSize: 14, backgroundColor: "#FAFAFA" }}>
                <p style={{ fontSize: 16, fontWeight: 400, marginLeft: "auto", marginRight: "auto", textAlign: "center", color: "black", marginTop: 30, paddingBottom: 18, paddingLeft: 15, paddingRight: 15, borderBottom: borderStyle }}>
                  Here are the current metrics for
                  {" "}
                  {dealName}
                  {" "}
                  as of
                  {" "}
                  {moment().format("MMMM Do, YYYY")}
                </p>
              </tr>
              <tr className="email-template-table-row">
                <table border="0" cellSpacing="0" width="100%">
                  <tr>
                    <td />
                    <td width="470">
                      <table border="0" cellSpacing="18" width="100%" style={{ marginTop: 12, marginBottom: -88 }}>
                        <tr>
                          <td style={{ fontSize: 20, color: "black", fontWeight: 400 }}>{getLabel1()}</td>
                          <td style={{ fontSize: 20, color: `#${companySecondaryColor}`, textAlign: "right", fontWeight: 500 }}>{getValue1()}</td>
                        </tr>
                        <tr>
                          <td style={{ fontSize: 20, color: "black", fontWeight: 400 }}>{getLabel2()}</td>
                          <td style={{ fontSize: 20, color: `#${companySecondaryColor}`, textAlign: "right", fontWeight: 500 }}>{getValue2()}</td>
                        </tr>
                      </table>
                    </td>
                    <td />
                  </tr>
                </table>
              </tr>
              <tr className="email-template-table-row" style={{ borderBottom: borderStyle }}>
                <p stlye={{ borderBottom: borderStyle }}>
                  <table style={{ margin: "0px auto", textAlign: "center", marginTop: 10, marginBottom: 10 }}>
                    <tr>
                      <td dangerouslySetInnerHTML={generateButtonHtml(companySecondaryColor)} />
                    </tr>
                  </table>
                </p>
              </tr>
              <tr className="email-template-table-row">
                <table width="100%" cellSpacing="0" style={{ borderTop: borderStyle, marginTop: -44 }}>
                  <tr>
                    <td style={{ textAlign: "center" }}><p style={{ fontWeight: 400 }}>This page is secure and requires the following pin for access:</p></td>
                  </tr>
                  <tr style={{ fontSize: 24, fontWeight: 500, color: `#${companySecondaryColor}` }}>
                    <td style={{ textAlign: "center" }}>
                      <p style={{ marginTop: 0, letterSpacing: ".2rem" }}>
                        {pin}
                      </p>
                    </td>
                  </tr>
                </table>
              </tr>
            </table>
          </div>
        </DoNotClick>
      </div>

      <div className="email-template-footer-container">
        <SecondaryButton
          size={ButtonSizeOptions.smFluid}
          label={`Preview ${campaignType} Report`}
          style={{ marginBottom: 10 }}
          onClick={() => {
            onPreviewPDFClicked();
          }}
        />
        <div className="email-template-footer-wrapper-right">
          <SecondaryButton
            label="Cancel"
            size={ButtonSizeOptions.smFluid}
            style={{ width: 132 }}
            onClick={onCancel}
          />
          <PrimaryButton
            disabled={sendButtonDisabled}
            label="Send"
            size={ButtonSizeOptions.smFluid}
            onClick={sendEmail}
            style={{ width: 132 }}
          />
        </div>
      </div>
    </div>
  );
};
const mapStateToProps = ({ contacts, emailCampaign }) => ({ ...contacts, emailCampaign });

export default compose(connect(mapStateToProps, {
  getPrepayment,
  getValuationSensitivity,
  getMasterContactList
}))(EmailTemplate);
